import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';

const useStyles = makeStyles(() => ({
  paper: {
    width: 600,
    maxWidth: 'calc(100vw - 32px)',
    borderRadius: 16,
    padding: '28px 32px 32px',
    fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    '& .MuiTypography-root, & button': {
      fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  title: {
    margin: 0,
    '&&': {
      fontFamily: "'Inter Tight', sans-serif",
    },
    fontSize: 20,
    fontWeight: 600,
    letterSpacing: '-.01em',
    lineHeight: 1.3,
    color: '#16141d',
  },
  description: {
    margin: '8px 0 0',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#3c4642',
  },
  closeButton: {
    flex: 'none',
    width: 32,
    height: 32,
    marginTop: -4,
    marginRight: -8,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: 8,
    background: 'none',
    color: '#5b6661',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f3f5f4',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 28,
  },
  button: {
    height: 44,
    padding: '0 20px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 10,
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  neutral: {
    border: '1px solid #cdd6d2',
    backgroundColor: '#ffffff',
    color: '#16141d',
    '&:hover:not(:disabled)': {
      backgroundColor: '#f3f5f4',
    },
  },
  primary: {
    border: '1px solid #0f8461',
    backgroundColor: '#0f8461',
    color: '#ffffff',
    '&:hover:not(:disabled)': {
      backgroundColor: '#0b6b4f',
    },
  },
}));

export type UnsavedChangesKind = 'payment' | 'billing' | 'both';

interface UnsavedChangesDialogProps {
  open: boolean;
  /** Which section(s) hold unsaved edits — worded into the description. */
  what: UnsavedChangesKind;
  /** "Save and pay" is running: buttons locked, spinner on the primary action. */
  busy?: boolean;
  onClose: () => void;
  onDiscardAndPay: () => void;
  onSaveAndPay: () => void;
}

/**
 * "Save your changes before paying?" (design Purchase Portal 2 [6.10], payinfo): shown when Pay
 * now is clicked while the Payment method and/or Billing information section is in edit mode
 * with unsaved changes. "Discard and pay" drops the edits and pays with what is on file;
 * "Save and pay" persists the edits first.
 */
export const UnsavedChangesDialog = ({
  open,
  what,
  busy = false,
  onClose,
  onDiscardAndPay,
  onSaveAndPay,
}: UnsavedChangesDialogProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const whatLabel = intl.formatMessage({ id: `page.order.confirmation.v2.unsaved.what.${what}` });

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      classes={{ paper: classes.paper }}
      aria-labelledby="unsaved-changes-title"
      aria-describedby="unsaved-changes-description"
    >
      <Box className={classes.header}>
        <Box minWidth={0}>
          <Typography component="h2" id="unsaved-changes-title" className={classes.title}>
            <FormattedMessage id="page.order.confirmation.v2.unsaved.title" />
          </Typography>
          <Typography component="p" id="unsaved-changes-description" className={classes.description}>
            <FormattedMessage
              id="page.order.confirmation.v2.unsaved.description"
              values={{ what: whatLabel }}
            />
          </Typography>
        </Box>
        <button
          type="button"
          id="unsaved-changes-close"
          className={classes.closeButton}
          onClick={onClose}
          disabled={busy}
          aria-label={intl.formatMessage({ id: 'page.order.confirmation.v2.unsaved.close' })}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </Box>
      <Box className={classes.actions}>
        <button
          type="button"
          id="unsaved-changes-discard"
          className={`${classes.button} ${classes.neutral}`}
          onClick={onDiscardAndPay}
          disabled={busy}
        >
          <FormattedMessage id="page.order.confirmation.v2.unsaved.discard" />
        </button>
        <button
          type="button"
          id="unsaved-changes-save"
          className={`${classes.button} ${classes.primary}`}
          onClick={onSaveAndPay}
          disabled={busy}
          aria-busy={busy}
        >
          {busy && <CircularProgress size={16} sx={{ color: 'currentColor' }} />}
          <FormattedMessage id="page.order.confirmation.v2.unsaved.save" />
        </button>
      </Box>
    </Dialog>
  );
};
